import { FunctionsHttpError } from "@supabase/supabase-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { STAFF_ELSEWHERE_SENTENCE, isStaffElsewhere, useSupabase, useTenant } from "@how/app-kit";
import type { Json } from "@how/database";
import { z } from "zod";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

/** A refusal from the database, with its SQLSTATE kept so the screen can put it in words. */
function rpcError(error: { message: string; code?: string }): Error & { code: string | undefined } {
  return Object.assign(new Error(error.message), { code: error.code });
}

// ------------------------------------------------------------------------------------------------ the team

/** One of the church's logins, with its roles and campuses (staff_campus_limits and church_logins, users.read). */
export interface TeamMember {
  readonly membershipId: string;
  readonly userId: string;
  /** The profile's name; empty when the person has not set one. */
  readonly displayName: string;
  /** The login's address, when the reader may see it (a child's is masked by church_logins). */
  readonly email: string | null;
  readonly status: string;
  /** Role keys, system and custom. */
  readonly roleKeys: readonly string[];
  /** Holds an owner's or administrator's role: sees every campus, cannot be limited. */
  readonly isPrivileged: boolean;
  /** Assigned campuses; empty means every campus. */
  readonly campusIds: readonly string[];
}

/**
 * The church's invited, active and suspended logins. Two reads in one: staff_campus_limits gives roles and campuses by
 * membership, church_logins gives the address by login. Both need users.read (owners and administrators).
 */
export function useTeam(enabled = true) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["team", tenantId],
    enabled: enabled && tenantId !== null,
    queryFn: async (): Promise<TeamMember[]> => {
      const [limits, logins] = await Promise.all([
        supabase.rpc("staff_campus_limits", { p_tenant_id: tenantId ?? "" }),
        supabase.rpc("church_logins", { p_tenant_id: tenantId ?? "" }),
      ]);
      if (limits.error) throw limits.error;
      if (logins.error) throw logins.error;
      const emails = new Map(logins.data.map((login) => [login.user_id, login.email]));
      return limits.data.map((row) => ({
        membershipId: row.membership_id,
        userId: row.user_id,
        displayName: row.display_name,
        email: emails.get(row.user_id) ?? null,
        status: row.status,
        roleKeys: row.roles,
        isPrivileged: row.is_privileged,
        campusIds: row.campus_ids,
      }));
    },
  });
}

function useTeamInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async () => {
    await client.invalidateQueries({ queryKey: ["team", tenantId] });
    // A role's holder count, and the caller's own permissions if they changed their own roles.
    await client.invalidateQueries({ queryKey: ["custom-roles", tenantId] });
    await client.invalidateQueries({ queryKey: ["authorize"] });
  };
}

/** Replaces a login's roles (set_membership_roles, users.manage). The owner guards are the database's. */
export function useSetMemberRoles() {
  const supabase = useSupabase();
  const invalidate = useTeamInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly membershipId: string;
      readonly roleKeys: readonly string[];
    }) => {
      const { error } = await supabase.rpc("set_membership_roles", {
        p_membership_id: input.membershipId,
        p_role_keys: [...input.roleKeys],
      });
      if (error) throw rpcError(error);
    },
    onSuccess: invalidate,
  });
}

/** Replaces a login's campuses; an empty list means every campus (set_staff_campuses, users.manage). */
export function useSetMemberCampuses() {
  const supabase = useSupabase();
  const invalidate = useTeamInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly membershipId: string;
      readonly campusIds: readonly string[];
    }) => {
      const { error } = await supabase.rpc("set_staff_campuses", {
        p_membership_id: input.membershipId,
        p_campus_ids: [...input.campusIds],
      });
      if (error) throw rpcError(error);
    },
    onSuccess: invalidate,
  });
}

/** What happens to a login: suspended (kept, cannot sign in), reactivated (from suspended), or removed (leaves). */
export type LoginStatusChange = "suspended" | "active" | "removed";

/**
 * Suspends, reactivates or removes a login (set_membership_status, users.manage). The guards are the database's:
 * never your own login, an owner only by an owner, never the last owner, active only from suspended.
 */
export function useSetMembershipStatus() {
  const supabase = useSupabase();
  const invalidate = useTeamInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly membershipId: string;
      readonly status: LoginStatusChange;
    }) => {
      const { error } = await supabase.rpc("set_membership_status", {
        p_membership_id: input.membershipId,
        p_status: input.status,
      });
      if (error) throw rpcError(error);
    },
    onSuccess: invalidate,
  });
}

/** Roles a member-portal login holds without being part of the team. */
const PORTAL_ROLES = new Set(["member", "guardian"]);

export interface InviteInput {
  readonly email: string;
  readonly roleKeys: readonly string[];
  /** Campuses to limit them to (multi-campus only); empty means every campus. */
  readonly campusIds: readonly string[];
}

export interface InviteOutcome {
  readonly membershipId: string;
  /** False when the invitation went out but the campuses could not be set; the dialog says so. */
  readonly campusesSet: boolean;
}

/**
 * Invites an address with the roles chosen (invite-member, users.manage): the roles are stored on the invited login
 * and are theirs once they accept. The email's link opens the staff dashboard when a role is a staff role, else the
 * member portal. Campuses are set on the invited login right after (set_staff_campuses).
 */
export function useInviteMember() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useTeamInvalidation();
  return useMutation({
    mutationFn: async (input: InviteInput): Promise<InviteOutcome> => {
      const staff = input.roleKeys.some((k) => !PORTAL_ROLES.has(k));
      const result = await supabase.functions.invoke<{ membership_id?: string }>("invite-member", {
        body: {
          tenant_id: tenantId,
          email: input.email.trim(),
          role_keys: [...input.roleKeys],
          surface: staff ? "staff_dashboard" : "member_portal",
        },
      });
      const failure: unknown = result.error;
      if (failure !== null) {
        let code = "";
        let message = failure instanceof Error ? failure.message : "";
        if (failure instanceof FunctionsHttpError) {
          const context: unknown = failure.context;
          const body =
            context instanceof Response
              ? ((await context.json().catch(() => null)) as {
                  error?: { code?: string; message?: string };
                } | null)
              : null;
          code = body?.error?.code ?? "";
          message = body?.error?.message ?? message;
        }
        throw Object.assign(new Error(message), { code });
      }
      const membershipId = result.data?.membership_id ?? "";
      if (input.campusIds.length === 0 || membershipId === "")
        return { membershipId, campusesSet: true };
      const { error } = await supabase.rpc("set_staff_campuses", {
        p_membership_id: membershipId,
        p_campus_ids: [...input.campusIds],
      });
      return { membershipId, campusesSet: !error };
    },
    onSuccess: invalidate,
  });
}

/** What invite-member refused, in words. */
export function describeInviteError(error: unknown): string {
  // One staff church per login (ADR 0068): the one sentence, whichever screen asked.
  if (isStaffElsewhere(error)) return STAFF_ELSEWHERE_SENTENCE;
  const e = (error ?? {}) as { code?: string; message?: string };
  const message = e.message ?? "";
  switch (e.code) {
    case "already_member":
      return "This address already has a login in your church. Find them in the list to change their roles.";
    case "forbidden":
      if (/tenant_owner/.test(message)) return "Only an owner can invite someone as an owner.";
      if (/privileged/.test(message))
        return "Only an owner or administrator can invite someone as an administrator.";
      if (/support session/.test(message))
        return "A DCX Logic support session cannot invite people. The church invites them.";
      return "You do not have permission to invite people.";
    case "no_surface_host":
      return "Your church's dashboard address is not set up yet. Contact DCX Logic support.";
    case "invalid_request":
      return "Check the email address, and choose between one and eight roles.";
    case "rejected":
      if (/at least one role/.test(message)) return "Choose at least one role.";
      return message.length > 0 ? message : "The invitation was not sent.";
    default:
      return "The invitation was not sent. Try again.";
  }
}

/** What set_membership_status refused, in words. */
export function describeStatusError(error: unknown): string {
  // One staff church per login (ADR 0068): the one sentence, whichever screen asked.
  if (isStaffElsewhere(error)) return STAFF_ELSEWHERE_SENTENCE;
  const e = (error ?? {}) as { code?: string; message?: string };
  const message = e.message ?? "";
  switch (e.code) {
    case "23514":
      if (/last active tenant owner/.test(message))
        return "The church needs at least one owner. Make someone else an owner first.";
      if (/only a suspended login/.test(message))
        return "Only a suspended login can be reactivated. Someone removed is invited again.";
      return message.length > 0 ? message : "The change breaks a rule and was not made.";
    case "42501":
      if (/own login/.test(message)) return "You cannot suspend or reactivate your own login.";
      if (/owner/.test(message)) return "Only an owner can suspend or remove an owner.";
      if (/privileged/.test(message))
        return "Only an owner or administrator can suspend or remove an administrator.";
      if (/support session/.test(message))
        return "A DCX Logic support session cannot change who can sign in. The church does that.";
      return "You do not have permission to make this change.";
    case "P0002":
      return "This login could not be found. It may have been removed; reload the page.";
    default:
      return "The change was not made. Try again.";
  }
}

// ------------------------------------------------------------------------------------------------ the campus limit

const campusLimitSchema = z.object({
  multiCampus: z.boolean(),
  campusLimitsStaff: z.boolean(),
  limited: z.boolean(),
  assignedCampusIds: z.array(z.string()),
});
export type CampusLimit = z.infer<typeof campusLimitSchema>;

/** Whether the church has multi-campus, and whether its switch "Limit staff to their campuses" is on. */
export function useCampusLimit() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["campus-limit", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<CampusLimit> => {
      const { data, error } = await supabase.rpc("my_campus_limit", {
        p_tenant_id: tenantId ?? "",
      });
      if (error) throw error;
      return campusLimitSchema.parse(data);
    },
  });
}

/** The church's switch (set_campus_limits_staff, settings.manage: the owner's). */
export function useSetCampusLimitsStaff() {
  const supabase = useSupabase();
  const client = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: async (on: boolean) => {
      const { error } = await supabase.rpc("set_campus_limits_staff", {
        p_tenant_id: tenantId ?? "",
        p_on: on,
      });
      if (error) throw rpcError(error);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["campus-limit", tenantId] });
    },
  });
}

/** The church's active campuses, for choosing a staff member's. */
export function useActiveCampuses(enabled = true) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["campuses", tenantId, "active-names"],
    enabled: enabled && tenantId !== null,
    queryFn: async (): Promise<{ id: string; name: string; isActive: boolean }[]> => {
      const { data, error } = await supabase
        .from("campuses")
        .select("id, name, is_active")
        .eq("tenant_id", tenantId ?? "")
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return data.map((row) => ({ id: row.id, name: row.name, isActive: row.is_active }));
    },
  });
}

// ------------------------------------------------------------------------------------------------ custom roles

export type Level = "none" | "view" | "edit" | "manage";

const levelSchema = z.enum(["none", "view", "edit", "manage"]);

const customRoleSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  description: z
    .string()
    .nullable()
    .transform((v) => v ?? ""),
  levels: z.record(z.string(), levelSchema),
  extras: z.array(z.string()),
  permissions: z.array(z.string()),
  holders: z.number(),
  updatedAt: z.string(),
});
export type CustomRole = z.infer<typeof customRoleSchema>;

const customRolesSchema = z.object({
  areas: z.array(z.string()),
  extras: z.array(z.string()),
  roles: z.array(customRoleSchema),
});
export type CustomRoles = z.infer<typeof customRolesSchema>;

/** The church's custom roles with their level sheets and holders (custom_roles, users.read). */
export function useCustomRoles(enabled = true) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["custom-roles", tenantId],
    enabled: enabled && tenantId !== null,
    queryFn: async (): Promise<CustomRoles> => {
      const { data, error } = await supabase.rpc("custom_roles", { p_tenant_id: tenantId ?? "" });
      if (error) throw error;
      return customRolesSchema.parse(data);
    },
  });
}

export interface CustomRoleInput {
  /** Null creates a role. */
  readonly roleId: string | null;
  readonly name: string;
  readonly description: string;
  /** Area to level; an area left out is none. */
  readonly levels: Readonly<Record<string, Level>>;
  readonly extras: readonly string[];
}

/** Creates or rewrites a custom role (set_custom_role, users.manage); every holder changes at once. */
export function useSaveCustomRole() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useTeamInvalidation();
  return useMutation({
    mutationFn: async (input: CustomRoleInput): Promise<string> => {
      // Only the areas above none are sent: a missing area is none, and the sheet stays short in the audit log.
      const levels = Object.fromEntries(
        Object.entries(input.levels).filter(([, level]) => level !== "none"),
      );
      const { data, error } = await supabase.rpc("set_custom_role", {
        p_tenant_id: tenantId ?? "",
        p_name: input.name.trim(),
        p_levels: levels as Json,
        p_extras: [...input.extras],
        p_description: input.description.trim(),
        ...(input.roleId === null ? {} : { p_role_id: input.roleId }),
      });
      if (error) throw rpcError(error);
      return data;
    },
    onSuccess: invalidate,
  });
}

/** Deletes a custom role nobody holds (delete_custom_role, users.manage). */
export function useDeleteCustomRole() {
  const supabase = useSupabase();
  const invalidate = useTeamInvalidation();
  return useMutation({
    mutationFn: async (roleId: string) => {
      const { error } = await supabase.rpc("delete_custom_role", { p_role_id: roleId });
      if (error) throw rpcError(error);
    },
    onSuccess: invalidate,
  });
}

// ------------------------------------------------------------------------------------------------ words

/**
 * What the database refused, in the words a church administrator reads. A broken rule (23514) carries its own
 * sentence from the database (the 30-role limit, a held role, the last owner, an owner's campuses), which is shown
 * as it is; the rest are named here.
 */
export function describeTeamError(error: unknown): string {
  // One staff church per login (ADR 0068): the one sentence, whichever screen asked.
  if (isStaffElsewhere(error)) return STAFF_ELSEWHERE_SENTENCE;
  const e = (error ?? {}) as { code?: string; message?: string };
  const message = e.message ?? "";
  switch (e.code) {
    case "23505":
      return "A role with this name already exists. Choose another name.";
    case "23514":
      if (/held by (\d+)/.test(message)) {
        const n = /held by (\d+)/.exec(message)?.[1] ?? "some";
        return `This role is still held by ${n} ${n === "1" ? "person" : "people"}. Give them another role first, then delete it.`;
      }
      if (/last active owner/.test(message))
        return "The church needs at least one owner. Make someone else an owner first.";
      if (/see every campus/.test(message))
        return "Owners and administrators see every campus; they cannot be limited to some.";
      if (/at least one role/.test(message)) return "Choose at least one role.";
      if (/at least one level/.test(message))
        return "Give the role at least one area above None, or one of the extras.";
      if (/at most 30/.test(message)) return "A church can have up to 30 custom roles.";
      return message.length > 0 ? message : "The change breaks a rule and was not saved.";
    case "42501":
      return /tenant_owner|owner/.test(message)
        ? "Only an owner can give or take away the owner role."
        : "You do not have permission to make this change.";
    case "P0002":
      return "This record could not be found. It may have been removed; reload the page.";
    default:
      return "The change was not saved. Try again.";
  }
}
